export type TwinPlanId = "core" | "scale" | "enterprise";

export type TwinPlan = {
  id: TwinPlanId;
  name: string;
  eyebrow: string;
  price: string;
  cadence?: string;
  monthlyPrice: number;
  description: string;
  detail: string;
};

export type CreditPack = {
  id: string;
  name: string;
  shortName: string;
  price: string;
  monthlyPrice: number;
  credits: number;
  description: string;
};

export const TWIN_PLANS = [
  {
    id: "core",
    name: "Core",
    eyebrow: "Start here",
    price: "From £1,000",
    cadence: "/ month",
    monthlyPrice: 1000,
    description:
      "A governed AI Twin for one focused team, built from the systems and business context you approve.",
    detail: "Managed deployment, standard onboarding and product support.",
  },
  {
    id: "scale",
    name: "Scale",
    eyebrow: "Grow adoption",
    price: "From £5,000",
    cadence: "/ month",
    monthlyPrice: 5000,
    description:
      "Extend the Twin across more teams, workflows and source systems with stronger rollout support.",
    detail: "Priced around organisational scope and operating requirements.",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    eyebrow: "Complex estates",
    price: "From £15,000",
    cadence: "/ month",
    monthlyPrice: 15000,
    description:
      "A wider deployment for complex organisations that need tailored governance, environments and support.",
    detail: "Architecture, governance and service model scoped together.",
  },
] satisfies [TwinPlan, TwinPlan, TwinPlan];

export const CREDIT_PACKS = [
  {
    id: "credits-none",
    name: "No credit pack",
    shortName: "None",
    price: "£0",
    monthlyPrice: 0,
    credits: 0,
    description: "Add model capacity when your first workflow is ready.",
  },
  {
    id: "credits-starter",
    name: "Starter credits",
    shortName: "Starter",
    price: "£250 / month",
    monthlyPrice: 250,
    credits: 25_000,
    description:
      "25,000 credits for one focused workflow or a small group of regular users.",
  },
  {
    id: "credits-team",
    name: "Team credits",
    shortName: "Team",
    price: "£750 / month",
    monthlyPrice: 750,
    credits: 100_000,
    description:
      "100,000 credits for several approved workflows and a wider group of users.",
  },
  {
    id: "credits-scale",
    name: "Scale credits",
    shortName: "Scale",
    price: "£1,800 / month",
    monthlyPrice: 1800,
    credits: 300_000,
    description:
      "300,000 credits for high-volume operations or committed model usage.",
  },
] satisfies [CreditPack, CreditPack, CreditPack, CreditPack];

export const FDE_DAY_RATE = 1200;
export const MAX_FDE_DAYS = 20;

export const FEATURE_ROWS = [
  {
    group: "Twin foundation",
    feature: "Dedicated, organisation-scoped MLX server",
    core: "EU or US region",
    scale: "EU or US region",
    enterprise: "EU or US, tailored environment",
  },
  {
    group: "Twin foundation",
    feature: "Governed answers with evidence and explicit limits",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Context",
    feature: "Approved source connectors, starting read-only",
    core: "Initial source set",
    scale: "Expanded source estate",
    enterprise: "Enterprise scope",
  },
  {
    group: "Context",
    feature: "Twin data refresh",
    core: "Daily scheduled refresh",
    scale: "Higher-frequency refresh",
    enterprise: "Cadence scoped to each source",
  },
  {
    group: "Context",
    feature: "Historical source import",
    core: "Up to 12 months per source",
    scale: "Extended history",
    enterprise: "Scope agreed per source",
  },
  {
    group: "Context",
    feature: "Reviewed and versioned business context",
    core: "Included",
    scale: "Multi-team context",
    enterprise: "Tailored governance",
  },
  {
    group: "Adoption",
    feature: "Teams and workflow scope",
    core: "Up to 10 users",
    scale: "Unlimited users",
    enterprise: "Unlimited users",
  },
  {
    group: "Adoption",
    feature: "Configured models for approved workflows",
    core: "Credit packs",
    scale: "Credit packs",
    enterprise: "Custom model routes",
  },
  {
    group: "Adoption",
    feature: "Third-party AI and developer access",
    core: "Configured access to your Twin",
    scale: "Configured access to your Twin",
    enterprise: "Configured access to your Twin",
  },
  {
    group: "Operations",
    feature: "Usage and provider-cost evidence where available",
    core: "Included",
    scale: "Included",
    enterprise: "Tailored reporting",
  },
  {
    group: "Operations",
    feature: "Product support",
    core: "Standard",
    scale: "Dedicated support channel",
    enterprise: "Dedicated channel and named service lead",
  },
  {
    group: "Delivery",
    feature: "Forward-deployed engineering",
    core: "Optional",
    scale: "Optional",
    enterprise: "Scoped to programme",
  },
] as const;
